import { useAuth } from '@/core/contexts/AuthContext';
import { useTranslation } from '@/core/hooks';
import { logger } from '@/core/logger';
import { deviceIdService } from '@/core/services/deviceIdService';
import { trackDynatraceEvent } from '@/core/services/dynatraceService';
import { trackCatalogEvent } from '@/core/services/trackDynatraceCatalog';
import { DT_SCREEN } from '@/core/constants/dynatraceScreens';
import { passcodeStorage } from '@/core/storage/passcodeStorage';
import { useAppStore, type User } from '@/core/stores/appStore';
import { useCallback, useState } from 'react';
import { Platform } from 'react-native';
import { authService } from '../services/authService';
import { passcodeService } from '@/modules/passcode/services/passcodeService';
import type { LoginCredentials } from '../types';

/** Catalogue events for UAE Pass (non-ADHA) flows — wired from `app/uaepass.tsx`. */
export function trackUaePassLoginError(message: string): void {
    trackCatalogEvent({
        screen: DT_SCREEN.UAE_PASS,
        actionName: 'uae_pass_login_error',
        eventType: 'error',
        properties: { p_failedMessage: message },
    });
}

export function trackUaePassLoginCancelled(): void {
    trackCatalogEvent({
        screen: DT_SCREEN.UAE_PASS,
        actionName: 'uae_pass_login_cancelled',
        eventType: 'action',
    });
}

/**
 * Custom hook for login business logic
 * Handles login flow, token persistence, and navigation
 */
export const useLogin = () => {
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const { t } = useTranslation();
    const { login: authLogin } = useAuth();
    const setAuth = useAppStore((state) => state.setAuth);

    /**
     * Handle login process with full business logic
     */
    const login = useCallback(async (credentials: LoginCredentials, redirectTo?: string): Promise<void> => {
        try {
            setIsLoading(true);
            setError(null);

            logger.info('useLogin:Starting login process', { email: credentials.email });

            // 1. Call API service
            const response = await authService.login(credentials);

            if (response.status !== 'success') {
                throw new Error(response.message || 'Login failed');
            }

            // 2. Extract token and user data
            const token = response.data.jwtToken;

            // Extract user data from response
            const user: User = {
                id: response.data.user?.id || credentials.email,
                primary_email: credentials.email,
                ...response.data.user, // Include any additional user fields from response
            };

            // 3. Clear all registration data on sign in (so welcome screen shows on next restart)
            logger.info('useLogin:Clearing all registration data on sign in');
            try {
                const { RegistrationStorage } = await import('@/modules/onboarding/registration/storage/registrationStorage');
                const { useManualRegistrationStore } = await import('@/modules/onboarding/registration/store/registrationStore');
                
                // Clear all registration data
                await RegistrationStorage.clearAll();
                await useManualRegistrationStore.getState().clearAll();
                const { useAppStore } = await import('@/core/stores/appStore');
                useAppStore.getState().clearUaePassRegistration();
                
                logger.info('useLogin:All registration data cleared');
            } catch (error) {
                logger.error('useLogin:Failed to clear registration data', error);
            }

            // 4. Check if this is a different user (new user login) and handle local passcode state
            // NOTE: customer_id is no longer used for passcode APIs. We use the user.id as identity.
            const previousUser = useAppStore.getState().auth.user;
            const isDifferentUser = !!previousUser && previousUser.id !== (response.data.user?.id || credentials.email);

            let hasLocalPasscodeData = await passcodeStorage.isPasscodeSet();

            if (isDifferentUser && hasLocalPasscodeData) {
                logger.info('useLogin:Detected different user login, clearing local passcode state', {
                    previousUserId: previousUser?.id,
                    newUserId: response.data.user?.id || credentials.email,
                });
                try {
                    await passcodeStorage.clearPasscodeState();
                    hasLocalPasscodeData = false;
                } catch (clearError) {
                    logger.warn('useLogin:Failed to clear passcode state for different user', clearError);
                }
            }

            // 5. Save to appStore (includes persistence) - also store login response for passcode flow
            const loginResponse = {
                ...(response.is_passcode_set !== undefined && { is_passcode_set: response.is_passcode_set }),
                ...(response.skip_passcode !== undefined && { skip_passcode: response.skip_passcode }),
            };
            setAuth(token, user, Object.keys(loginResponse).length > 0 ? loginResponse : undefined);

            // 6. Check if first time login - navigate to change password screen
            const isFirstTimeLogin = response.is_first_time_login === true;

            if (isFirstTimeLogin) {
                logger.info('useLogin:First time login detected, navigating to change password', {
                    userId: user.id,
                    email: user.primary_email,
                });
                // Navigate to change password screen for first-time login (will show current password screen first)
                const { router } = await import('expo-router');
                // Use replace instead of dismissAll + replace to avoid navigation errors
                try {
                    if (router.canGoBack()) {
                        router.dismissAll();
                    }
                } catch (error) {
                    // Ignore dismissAll errors - replace will handle navigation
                    logger.debug('useLogin', 'dismissAll failed, using replace only', error);
                }
                router.replace('/change-password');
                return; // Don't call authLogin - change password will handle navigation
            }

            // 7. Handle passcode flow for regular login (not first time)
            const isPasscodeSetInResponse = response.is_passcode_set === true;
            const skipPasscodeFlag = response.skip_passcode === true;

            logger.info('useLogin:Passcode flow check', {
                isPasscodeSetInResponse,
                skipPasscodeFlag,
                hasLocalPasscodeData,
            });

            // Handle skip_passcode: true scenario (normal journey only)
            if (skipPasscodeFlag) {
                logger.info('useLogin:Skip passcode detected, handling skip flow');
                
                // If local passcode data exists (different account scenario), clear it
                if (hasLocalPasscodeData) {
                    logger.info('useLogin:Clearing local passcode data (different account)');
                    await passcodeStorage.clearPasscodeState();
                }

                // Navigate to home - skip passcode API will be called there
                // (HomeScreen ignores this for ADHA; service also blocks the API)
                const { router } = await import('expo-router');
                try {
                    if (router.canGoBack()) {
                        router.dismissAll();
                    }
                } catch (error) {
                    logger.debug('useLogin', 'dismissAll failed, using replace only', error);
                }
                if (useAppStore.getState().isAdhaJourney) {
                    router.replace('/(auth)/(home)' as any);
                    return;
                }
                // Pass skip_passcode flag via route params
                router.replace('/(auth)/(home)?skipPasscode=true' as any);
                return;
            }

            // Special case: login was triggered from passcode-login screen (\"Continue with email\")
            // In this case, even if local passcode data exists, we still want to go back to passcode-login
            // so the user must enter/pass or explicitly skip passcode to get a passcode token.
            if (redirectTo === 'passcode-login' && isPasscodeSetInResponse && !skipPasscodeFlag) {
                logger.info('useLogin:Redirect from passcode-login, forcing passcode-login screen again');
                try {
                    // Ensure local passcode state is marked as set (biometric disabled by default)
                    await passcodeStorage.setPasscodeState(false);
                    logger.info('useLogin:Passcode state ensured for redirect from passcode-login');
                } catch (err) {
                    logger.warn('useLogin:Failed to ensure passcode state on redirect from passcode-login', err);
                }

                const { router } = await import('expo-router');
                try {
                    if (router.canGoBack()) {
                        router.dismissAll();
                    }
                } catch (error) {
                    logger.debug('useLogin', 'dismissAll failed, using replace only (redirect from passcode-login)', error);
                }
                router.replace('/passcode-login' as any);
                return;
            }

            // Handle is_passcode_set: false AND skip_passcode: false
            // This includes the case after reset passcode
            if (!isPasscodeSetInResponse && !skipPasscodeFlag) {
                // On web, automatically skip passcode instead of showing setup screen
                // ADHA sessions never use skip-passcode (UAE Pass / Emirates ID OTP).
                if (Platform.OS === 'web' && !useAppStore.getState().isAdhaJourney) {
                    logger.info('useLogin:Web platform detected, auto-skipping passcode', {
                        isPasscodeSetInResponse,
                        skipPasscodeFlag,
                        hasLocalPasscodeData,
                    });
                    
                    // Clear local passcode data if it exists
                    if (hasLocalPasscodeData) {
                        logger.info('useLogin:Clearing local passcode data before web auto-skip');
                        await passcodeStorage.clearPasscodeState();
                    }
                    
                    try {
                        // Get device ID and call skip passcode API
                        const deviceId = await deviceIdService.getDeviceId();
                        logger.info('useLogin:Calling skip passcode API for web', { deviceId });
                        
                        const skipResponse = await passcodeService.skipPasscode(deviceId, 'login');
                        
                        if (skipResponse.status === 'success' && skipResponse.accessToken) {
                            // Store the passcode token from skip response
                            const setPasscodeToken = useAppStore.getState().setPasscodeToken;
                            setPasscodeToken(skipResponse.accessToken);
                            logger.info('useLogin:Skip passcode token stored for web');
                        }
                        
                        logger.info('useLogin:Web auto skip passcode successful, navigating to home');
                    } catch (skipError) {
                        logger.error('useLogin:Web auto skip passcode failed', { error: skipError });
                        // Continue with navigation even if skip fails - user can still use the app
                    }
                    
                    // Navigate to home - skip passcode API has been called
                    const { router } = await import('expo-router');
                    try {
                        if (router.canGoBack()) {
                            router.dismissAll();
                        }
                    } catch (error) {
                        logger.debug('useLogin', 'dismissAll failed, using replace only', error);
                    }
                    router.replace('/(auth)/(home)?skipPasscode=true' as any);
                    return;
                }

                if (Platform.OS === 'web' && useAppStore.getState().isAdhaJourney) {
                    logger.info('useLogin:ADHA journey — skip-passcode not called');
                    const { router } = await import('expo-router');
                    try {
                        if (router.canGoBack()) {
                            router.dismissAll();
                        }
                    } catch (error) {
                        logger.debug('useLogin', 'dismissAll failed, using replace only', error);
                    }
                    router.replace('/(auth)/(home)' as any);
                    return;
                }
                
                // Mobile: Navigate to passcode setup screen
                logger.info('useLogin:Passcode not set (or reset), navigating to passcode setup', {
                    isPasscodeSetInResponse,
                    skipPasscodeFlag,
                    hasLocalPasscodeData,
                });
                
                // Clear local passcode data if it exists (e.g., after reset passcode)
                if (hasLocalPasscodeData) {
                    logger.info('useLogin:Clearing local passcode data before setup');
                    await passcodeStorage.clearPasscodeState();
                }
                
                // Save setup required flag so on app restart, set passcode screen appears
                // NOTE: We do not depend on customer_id here - backend identifies user via token
                try {
                    await passcodeStorage.setSetupRequired();
                    logger.info('useLogin:Setup required flag saved for app restart');
                } catch (err) {
                    logger.warn('useLogin:Failed to save setup required flag', err);
                }
                
                const { router } = await import('expo-router');
                try {
                    if (router.canGoBack()) {
                        router.dismissAll();
                    }
                } catch (error) {
                    logger.debug('useLogin', 'dismissAll failed, using replace only', error);
                }
                router.replace('/passcode?from=login&mode=setup' as any);
                return;
            }

            // Handle is_passcode_set: true but no local passcode data
            if (isPasscodeSetInResponse && !hasLocalPasscodeData) {
                logger.info('useLogin:Passcode set but no local data, navigating to passcode login');
                
                // Save passcode state to local storage immediately (fresh login scenario)
                // This ensures profile settings show passcode is set, and on restart passcode login appears
                try {
                    // Default biometric to false for fresh login
                    await passcodeStorage.setPasscodeState(false);
                    logger.info('useLogin:Passcode state saved to local storage (fresh login)');
                } catch (saveError) {
                    logger.warn('useLogin:Failed to save passcode state on fresh login', saveError);
                    // Don't block navigation if save fails
                }
                
                const { router } = await import('expo-router');
                try {
                    if (router.canGoBack()) {
                        router.dismissAll();
                    }
                } catch (error) {
                    logger.debug('useLogin', 'dismissAll failed, using replace only', error);
                }
                router.replace('/passcode-login' as any);
                return;
            }

            // 8. Inform AuthContext with optional redirect target (normal flow)
            authLogin(redirectTo);

            trackDynatraceEvent({
                p_screen: 'login',
                p_actionName: 'User Login Success',
                eventType: 'action',
            });
            logger.info('useLogin:Login successful', {
                userId: user.id,
                email: user.primary_email,
                isFirstTime: isFirstTimeLogin,
                isPasscodeSet: isPasscodeSetInResponse,
                skipPasscode: skipPasscodeFlag,
            });

        } catch (err) {
            // Provide generic error message for failed login
            const errorMessage = t('auth.loginError');
            setError(errorMessage);
            
            const failedMessage = err instanceof Error ? err.message : String(err);
            trackDynatraceEvent({
                p_screen: 'login',
                p_actionName: 'User Login Failed',
                p_failedMessage: failedMessage,
                eventType: 'error',
            });
            logger.error('useLogin:Login failed', err);
            throw new Error(errorMessage);
        } finally {
            setIsLoading(false);
        }
    }, [t, authLogin, setAuth]);

    /**
     * Clear error state
     */
    const clearError = useCallback(() => {
        setError(null);
    }, []);

    return {
        // State
        isLoading,
        error,

        // Actions
        login,
        clearError,
    };
};
